import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import "./BankOTPModal.scss";

interface BankOTPModalProps {
  OTPModal: boolean | number;
  setOTPModal: (value: boolean | number) => void;
  resendOTP: () => void;
  OTPField: string[];
  setOTPField: (value: string[]) => void;
  inputRefs: React.MutableRefObject<any[]>;
  handleChangeOTP: (index: number, e: React.ChangeEvent<HTMLInputElement>) => void;
  counter: number;
  setCounter: (value: number) => void;
  verifyEmail?: ((step: number) => void) | null;
  newEmail?: string | null;
  openInModal?: boolean;
}

export const BankOTPModal: React.FC<BankOTPModalProps> = ({
  OTPModal,
  setOTPModal,
  resendOTP,
  OTPField,
  setOTPField,
  inputRefs,
  handleChangeOTP,
  counter,
  setCounter,
  verifyEmail,
  newEmail,
  openInModal = true,
}) => {
  const { t } = useTranslation();

  useEffect(() => {
    if (OTPModal === 1 || OTPModal === 2 || OTPModal === true) {
      if (inputRefs.current[0]) {
        inputRefs.current[0].focus();
      }
    }
  }, [OTPModal, inputRefs]);

  useEffect(() => {
    if (OTPModal === 2 && inputRefs.current[0]) {
      inputRefs.current[0].focus();
    }
  }, [OTPModal, inputRefs]);

  useEffect(() => {
    let intervalId: NodeJS.Timeout;
    if (counter > 0) {
      intervalId = setInterval(() => {
        setCounter(counter - 1);
      }, 1000);
    }
    return () => clearInterval(intervalId);
  }, [counter, setCounter]);

  const handleKeyDownOTP = (index: number, e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Backspace" && !OTPField[index] && index > 0 && inputRefs.current[index - 1]) {
      inputRefs.current[index - 1].focus();
    }
  };

  const handleSendOTP = async () => {
    for (let i = 0; i < OTPField.length; i++) {
      await handleChangeOTP(i, { target: { value: OTPField[i] } } as React.ChangeEvent<HTMLInputElement>);
    }

    if (OTPModal === 1) {
      verifyEmail?.(2);
      setOTPModal(2);
      setOTPField(new Array(4).fill(""));
    } else if (OTPModal === 2) {
      setOTPModal(0);
      setOTPField(new Array(4).fill(""));
      setCounter(300);
    }
  };

  const isOTPComplete = OTPField.every((item) => item && item.length === 1);

  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, "0")}`;
  };

  if (!(OTPModal === 1 || OTPModal === 2 || OTPModal === true)) {
    return null;
  }

  return (
    <div className="bank-otp-modal-overlay">
      <div className="bank-otp-modal">
        <div className="bank-otp-modal__header">
          <h3 className="bank-otp-modal__title">{t("Verify OTP")}</h3>
          <button
            className="bank-otp-modal__close"
            onClick={() => {
              setOTPModal(0);
              setOTPField(new Array(4).fill(""));
              setCounter(300);
            }}
            type="button"
          >
            ×
          </button>
        </div>

        <div className="bank-otp-modal__banner" style={{display:'grid', background: "#edebf5"}}>
          {/* <div className="bank-otp-modal__banner-icon">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
              <path
                d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41L9 16.17z"
                fill="currentColor"
              />
            </svg>
          </div> */}
          <span className="bank-otp-modal__banner-text" style={{textAlign:'center', color: 'black'}}>{t("Verify your current email")}</span>
        </div>

        <div className="bank-otp-modal__body">
          <h4 className="bank-otp-modal__step" style={{textAlign:'center'}}>{t("Step 2: Verify your current email")}</h4>
          
          <p className="bank-otp-modal__description" style={{textAlign:'center'}}>
            {OTPModal === 2
              ? `${t("Please check your new email inbox")} ${newEmail} ${t("for the one-time code.")}`
              : t("Enter the OTP sent to your current email address")}
          </p>

          <div className="bank-otp-modal__inputs" style={{marginTop:'28px'}}>
            {OTPField?.map((item, index) => (
              <input
                key={index}
                type="text"
                value={item}
                ref={(input) => (inputRefs.current[index] = input)}
                onKeyDown={(e) => handleKeyDownOTP(index, e)}
                onChange={(e) => {
                  const val = e.target.value.replace(/[^0-9a-zA-Z]/g, "").slice(0, 1);
                  const newOTP = [...OTPField];
                  newOTP[index] = val;
                  setOTPField(newOTP);
                  if (val && index < OTPField.length - 1) {
                    inputRefs.current[index + 1]?.focus();
                  }
                }}
                className="bank-otp-modal__input"
                maxLength={1}
              />
            ))}
          </div>

          <div className="bank-otp-modal__footer">
            <button
              className="bank-otp-modal__verify-btn"
              onClick={handleSendOTP}
              disabled={!isOTPComplete}
            >
              {t("Verify")}
            </button>

            <div className="bank-otp-modal__resend">
              {counter > 0 ? (
                <span>{t("Resend in")} {formatTime(counter)}</span>
              ) : (
                <button
                  className="bank-otp-modal__resend-btn"
                  onClick={() => {
                    setCounter(300);
                    resendOTP();
                  }}
                >
                  {t("Resend")}
                </button>
              )}
            </div>

            <button
              className="bank-otp-modal__cancel-btn"
              onClick={() => {
                setOTPModal(0);
                setOTPField(new Array(4).fill(""));
                setCounter(300);
              }}
            >
              {t("Cancel")}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};
